import React, { useEffect, useMemo, useRef, useState } from "react";
import "./DailyPuzzle.css";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import { format } from "../Shared/i18n/format.ts";
import { track } from "../Shared/analytics.ts";
import { Level, Tile, dailySeed, generateBoard, gradeOf, practiceSeed, puzzleNumber } from "./equalGroups.ts";
import {
    CheckResult,
    GROUP_SIZE,
    MAX_MISTAKES,
    check,
    clearSelection,
    guessIds,
    newGame,
    remainingIds,
    replay,
    shareText,
    toggleTile,
    unfoundGroups,
} from "./gameState.ts";
import { currentStreak, readProgress, recordSolved, saveProgress } from "./store.ts";
import { dailyPuzzleEventParams } from "./eventParams.ts";
import { createRng } from "./rng.ts";

/*
 * The Equal Groups board (ADR 0127): the daily puzzle for a date and grade, or a
 * practice board dealt from a random seed. The rules are gameState.ts, the board
 * is equalGroups.ts, what is kept on the device is store.ts; this is the screen.
 *
 * Built in the first render, from the date and the saved guesses, so the board
 * is there when the page paints and nothing moves when it arrives (ADR 0067). A
 * practice board never touches the store.
 */

interface EqualGroupsBoardProps {
    level: Level;
    /** Today's local date, YYYY-MM-DD. */
    date: string;
    /** The page's address, for the shared result. */
    url: string;
}

/** The message after a check: what the page says, and a screen reader announces. */
function useResultMessage() {
    const t = useTranslation();
    return (result: CheckResult, ended: "won" | "lost" | null): string => {
        const parts: string[] = [];
        if (result.kind === "repeat") {
            parts.push(t("dailyPuzzle.repeatGuess"));
        } else if (result.kind === "found") {
            parts.push(format(t("dailyPuzzle.foundAnnounceTemplate"), { value: result.value }));
        } else if (result.kind === "wrong") {
            parts.push(
                result.maxEqual >= 3
                    ? t("dailyPuzzle.wrongThree")
                    : result.maxEqual === 2
                    ? t("dailyPuzzle.wrongTwo")
                    : t("dailyPuzzle.wrongNone")
            );
            if (!result.gameOver) {
                parts.push(
                    result.mistakesLeft === 1
                        ? t("dailyPuzzle.mistakeLeftOne")
                        : format(t("dailyPuzzle.mistakesLeftTemplate"), { n: result.mistakesLeft })
                );
            }
        }
        if (ended) {
            parts.push(ended === "won" ? t("dailyPuzzle.won") : t("dailyPuzzle.lost"));
        }
        return parts.join(" ");
    };
}

export function EqualGroupsBoard({ level, date, url }: EqualGroupsBoardProps) {
    const [practice, setPractice] = useState<string | null>(null);
    const daily = practice === null;
    const seed = daily ? dailySeed(date, level) : practice;
    return (
        <Play
            key={seed}
            seed={seed}
            level={level}
            date={date}
            daily={daily}
            url={url}
            onPractice={() => setPractice(practiceSeed(level))}
            onBackToDaily={() => setPractice(null)}
        />
    );
}

interface PlayProps {
    seed: string;
    level: Level;
    date: string;
    daily: boolean;
    url: string;
    onPractice: () => void;
    onBackToDaily: () => void;
}

function Play({ seed, level, date, daily, url, onPractice, onBackToDaily }: PlayProps) {
    const t = useTranslation();
    const describe = useResultMessage();
    const board = useMemo(() => generateBoard(seed, level), [seed, level]);
    const tiles = useMemo(() => {
        const map = new Map<string, Tile>();
        board.groups.forEach((group) => group.tiles.forEach((tile) => map.set(tile.id, tile)));
        return map;
    }, [board]);

    const [game, setGame] = useState(() => (daily ? replay(board, readProgress(date, level)) : newGame(board)));
    const [layout, setLayout] = useState(board.order);
    const [message, setMessage] = useState("");
    const [focusId, setFocusId] = useState<string | null>(null);
    const [streak, setStreak] = useState(() => (daily ? currentStreak(level, date) : 0));

    const [copyState, setCopyState] = useState<"idle" | "copied" | "manual">("idle");
    const copyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const manualInput = useRef<HTMLTextAreaElement>(null);

    const tileRefs = useRef(new Map<string, HTMLButtonElement>());
    const endRef = useRef<HTMLDivElement>(null);
    const [focusTick, setFocusTick] = useState(0);
    const [endTick, setEndTick] = useState(0);

    const playing = game.status === "playing";
    const remaining = useMemo(() => {
        const left = new Set(remainingIds(game));
        return layout.filter((id) => left.has(id));
    }, [game, layout]);
    const activeId = focusId && remaining.includes(focusId) ? focusId : remaining[0];
    const activeRef = useRef(activeId);
    activeRef.current = activeId;

    useEffect(() => () => clearTimeout(copyTimer.current), []);

    // After a check the chosen tiles may be gone, taking the focus with them.
    useEffect(() => {
        if (focusTick > 0 && activeRef.current) {
            tileRefs.current.get(activeRef.current)?.focus();
        }
    }, [focusTick]);

    useEffect(() => {
        if (endTick > 0) {
            endRef.current?.focus();
        }
    }, [endTick]);

    useEffect(() => {
        if (copyState === "manual") {
            manualInput.current?.focus();
            manualInput.current?.select();
        }
    }, [copyState]);

    function toggle(id: string) {
        setGame((current) => toggleTile(current, id));
        setMessage("");
    }

    function runCheck() {
        const { state, result } = check(game);
        if (result.kind === "ignored") {
            return;
        }
        setGame(state);
        const ended = state.status === "playing" ? null : state.status;
        setMessage(describe(result, ended));
        if (daily && result.kind !== "repeat") {
            saveProgress(date, level, guessIds(state));
        }
        if (ended) {
            // After the stores, as the quiz does: analytics never costs a streak.
            if (daily && ended === "won") {
                setStreak(recordSolved(level, date).count);
            }
            track("quiz_completed", dailyPuzzleEventParams(level, daily, state.found.length, state.mistakes));
            setEndTick((n) => n + 1);
        } else {
            setFocusTick((n) => n + 1);
        }
    }

    function shuffle() {
        setLayout(createRng(String(Math.random())).shuffle(layout));
    }

    function onGridKeyDown(event: React.KeyboardEvent) {
        const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: GROUP_SIZE, ArrowUp: -GROUP_SIZE }[event.key];
        if (step === undefined) {
            return;
        }
        const current = remaining.find((id) => tileRefs.current.get(id) === event.target);
        if (current === undefined) {
            return;
        }
        event.preventDefault();
        const next = Math.min(remaining.length - 1, Math.max(0, remaining.indexOf(current) + step));
        const id = remaining[next];
        setFocusId(id);
        tileRefs.current.get(id)?.focus();
    }

    const levelLabel = format(t("dailyPuzzle.gradeTemplate"), { n: gradeOf(level) });
    const number = Math.max(1, puzzleNumber(date));
    const resultText = shareText(
        format(t("dailyPuzzle.resultHeadingTemplate"), { n: number, level: levelLabel }),
        game,
        url
    );

    async function copyResult() {
        try {
            // Undefined outside a secure context, and can reject when the
            // document is not focused or permission is refused.
            await navigator.clipboard.writeText(resultText);
            setCopyState("copied");
            clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopyState("idle"), 6000);
        } catch {
            // Rather than claim a copy that did not happen, show the text.
            setCopyState("manual");
        }
    }

    const mistakesLeft = MAX_MISTAKES - game.mistakes;
    const missed = unfoundGroups(game);

    return (
        <div className="dpPlay">
            <h2 className="dpTitle">
                {daily ? format(t("dailyPuzzle.titleTemplate"), { n: number }) : t("dailyPuzzle.practiceTitle")}
            </h2>
            <p className="dpHow">{t("dailyPuzzle.howToPlay")}</p>

            <div className="dpMistakes">
                <span aria-hidden="true">{t("dailyPuzzle.mistakesLabel")}</span>
                <span className="dpDots" aria-hidden="true">
                    {Array.from({ length: MAX_MISTAKES }, (_, i) => (
                        <span key={i} className={i < mistakesLeft ? "dpDot dpDotOn" : "dpDot"} />
                    ))}
                </span>
                <span className="srOnly">
                    {format(t("dailyPuzzle.mistakesAriaTemplate"), { n: mistakesLeft, total: MAX_MISTAKES })}
                </span>
            </div>

            <div className="dpBoard" role="group" aria-label={t("dailyPuzzle.boardAria")} onKeyDown={onGridKeyDown}>
                {game.found.map((groupIndex) => {
                    const group = board.groups[groupIndex];
                    return (
                        <div key={groupIndex} className="dpFound">
                            <p className="dpFoundValue">{format(t("dailyPuzzle.foundValueTemplate"), { value: group.value })}</p>
                            <ul className="dpFoundTiles">
                                {group.tiles.map((tile) => (
                                    <li key={tile.id}>{tile.text}</li>
                                ))}
                            </ul>
                        </div>
                    );
                })}

                {playing ? (
                    <div className="dpGrid">
                        {remaining.map((id) => {
                            const selected = game.selected.includes(id);
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    className={selected ? "dpTile dpTileOn" : "dpTile"}
                                    aria-pressed={selected}
                                    tabIndex={id === activeId ? 0 : -1}
                                    ref={(element) => {
                                        if (element) {
                                            tileRefs.current.set(id, element);
                                        } else {
                                            tileRefs.current.delete(id);
                                        }
                                    }}
                                    onFocus={() => setFocusId(id)}
                                    onClick={() => toggle(id)}
                                >
                                    {tiles.get(id)!.text}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    game.status === "lost" && (
                        <>
                            <p className="dpMissedHeading">{t("dailyPuzzle.missedHeading")}</p>
                            {missed.map((group) => (
                                <div key={group.value} className="dpFound dpMissed">
                                    <p className="dpFoundValue">
                                        {format(t("dailyPuzzle.foundValueTemplate"), { value: group.value })}
                                    </p>
                                    <ul className="dpFoundTiles">
                                        {group.tiles.map((tile) => (
                                            <li key={tile.id}>{tile.text}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </>
                    )
                )}
            </div>

            <p className="dpMessage" role="status" aria-live="polite">
                {message}
            </p>

            {playing ? (
                <div className="dpActions">
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={game.selected.length !== GROUP_SIZE}
                        onClick={runCheck}
                    >
                        {t("dailyPuzzle.check")}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={game.selected.length === 0}
                        onClick={() => setGame(clearSelection(game))}
                    >
                        {t("dailyPuzzle.clear")}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={shuffle}>
                        {t("dailyPuzzle.shuffle")}
                    </button>
                </div>
            ) : (
                <div className="dpEnd" ref={endRef} tabIndex={-1}>
                    <p className="dpEndHeading">
                        {game.status === "won" ? t("dailyPuzzle.won") : t("dailyPuzzle.lost")}
                    </p>
                    {daily && game.status === "won" && streak > 0 && (
                        <p className="dpStreak">
                            {streak === 1
                                ? t("dailyPuzzle.streakOne")
                                : format(t("dailyPuzzle.streakTemplate"), { n: streak })}
                        </p>
                    )}
                    <div className="dpActions">
                        {daily && (
                            <button type="button" className="btn btn-primary" onClick={copyResult}>
                                {t("dailyPuzzle.copyResult")}
                            </button>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={onPractice}>
                            {daily ? t("dailyPuzzle.practice") : t("dailyPuzzle.practiceAnother")}
                        </button>
                        {!daily && (
                            <button type="button" className="btn btn-secondary" onClick={onBackToDaily}>
                                {t("dailyPuzzle.backToDaily")}
                            </button>
                        )}
                    </div>
                    <div role="status" aria-live="polite" className="dpCopyStatus">
                        {copyState === "copied" && <span>{t("dailyPuzzle.copied")}</span>}
                        {copyState === "manual" && <span>{t("dailyPuzzle.copyManual")}</span>}
                    </div>
                    {copyState === "manual" && (
                        <textarea
                            ref={manualInput}
                            className="input dpCopyInput"
                            readOnly
                            rows={3}
                            value={resultText}
                            aria-label={t("dailyPuzzle.copyAria")}
                            onFocus={(event) => event.target.select()}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

export default EqualGroupsBoard;
